import * as Dialog from '@radix-ui/react-dialog';
import React, { useCallback, useState } from 'react';
import AccessibleModal, {
    AccessibleModalCloseButton,
} from '../AccessibleModal';
import { ErrorComponent } from '../../forms/fields/FormField';
import { gettext, pgettext } from '../../utils/text';

interface TermsModalProps {
    isOpen: boolean;
    toggleModal: () => void;
}

const TermsModal = ({ isOpen, toggleModal }: TermsModalProps) => {
    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                toggleModal();
            }
        },
        [toggleModal],
    );

    const dialogContentId = 'terms-info-dialog';

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <AccessibleModal
                className="position-relative p-4 p-md-5"
                contentStyle={{ maxWidth: '640px', width: '100%' }}
            >
                <div id={dialogContentId}>
                    <AccessibleModalCloseButton
                        className="btn-close position-absolute top-0 end-0 m-3"
                        aria-label={gettext('Close')}
                    />
                    <Dialog.Title asChild>
                        <h2 className="h4 mb-3">
                            {gettext('Terms and conditions of orders')}
                        </h2>
                    </Dialog.Title>
                    <Dialog.Description asChild>
                        <div
                            className="text-body"
                            dangerouslySetInnerHTML={{
                                __html: pgettext(
                                    'intermission-order-terms',
                                    '<ol>' +
                                        '<li>To place an order, you need to have tickets for a performance by the day of placing the order.</li>' +
                                        '<li>We accept orders no later than <b>24 hours before</b> the performance.</li>' +
                                        '<li>Orders for <b>more than 20 people</b> should be sent no later than <b>3 working days before</b> the performance to the email address <a href="mailto:catering@opera.ee">catering@opera.ee</a></li>' +
                                        '<li>The order is deemed to be accepted from the moment of receiving the payment.</li>' +
                                        '<li>Orders are processed in the order in which they are received.</li>' +
                                        '<li>Once the order has been accepted by the theatre, no changes can be made to the order (change of date, products, etc.). ' +
                                        'The theatre will not refund the fee paid for an accepted order if the customer has made a mistake when placing the order (wrong date, wrong products, etc.).</li>' +
                                        '<li>Orders are served on-site at the theatre either before the performance or during the intermission. Orders are not available for takeaway.</li>' +
                                        '<li>The order will be served during the first intermission if the show has two intermissions.</li>' +
                                        '<li>The order for a performance in the theatre hall is usually served to the orderer (by name) in the location written in the confirmation email. ' +
                                        'The catering service of the Estonian National Opera reserves the right to change the location of the order if necessary.</li>' +
                                        '<li>The photos are illustrative. For more detailed information, please contact <a href="mailto:catering@opera.ee">catering@opera.ee</a>.</li>' +
                                        '</ol>',
                                ),
                            }}
                        />
                    </Dialog.Description>
                </div>
            </AccessibleModal>
        </Dialog.Root>
    );
};

interface TermsAndConditionsProps {
    handleBlur: (e: React.FocusEvent) => void;
    terms: boolean;
    setFieldValue: (
        field: string,
        value: boolean,
        shouldValidate?: boolean,
    ) => void;
    setFieldTouched: (
        field: string,
        touched?: boolean,
        shouldValidate?: boolean,
    ) => void;
    isFieldInvalid: (field: string) => boolean;
}

const TermsAndConditions = ({
    handleBlur,
    terms,
    setFieldValue,
    setFieldTouched,
    isFieldInvalid,
}: TermsAndConditionsProps) => {
    const [popoverTermsOpen, setPopoverTermsOpen] = useState(false);
    const toggleTermsPopover = () => setPopoverTermsOpen(!popoverTermsOpen);
    return (
        <div className="checkout__terms">
            <label
                className="form-check-label checkout__terms-label px-4"
                htmlFor="terms-checkbox"
            >
                <input
                    id="terms-checkbox"
                    name="terms"
                    type="checkbox"
                    className="form-check-input"
                    checked={terms}
                    onChange={e => {
                        setFieldTouched('terms', true, false);
                        setFieldValue('terms', e.target.checked, true);
                    }}
                    onBlur={handleBlur}
                    aria-describedby={
                        isFieldInvalid('terms')
                            ? 'terms-error terms-info-hint'
                            : 'terms-info-hint'
                    }
                    aria-required="true"
                    aria-invalid={isFieldInvalid('terms') ? 'true' : 'false'}
                    required
                />
                <span>{gettext('I have read and agree with')}</span>
            </label>
            <button
                type="button"
                id="terms-info-button"
                className="checkout__terms-modal-label link text-danger bg-transparent btn-link border-0"
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleTermsPopover();
                }}
                aria-label={gettext('Show additional order information')}
                aria-haspopup="dialog"
                aria-expanded={popoverTermsOpen}
                aria-controls="terms-info-dialog"
            >
                {gettext(' additional order information')}
            </button>
            <span id="terms-info-hint" className="visually-hidden">
                {gettext(
                    'Additional order information opens in a dialog via the adjacent button.',
                )}
            </span>
            <TermsModal
                isOpen={popoverTermsOpen}
                toggleModal={toggleTermsPopover}
            />

            <ErrorComponent name="terms" className="mt-2" id="terms-error" />
        </div>
    );
};

export default TermsAndConditions;
